import { useEffect, useRef, useMemo, useState } from "react";
import {
  ArrowDownToLine,
  ArrowUpRight,
  Check,
  ChevronRight,
  MessageSquare,
  Star,
  X,
} from "lucide-react";
import {
  feedbackMetrics,
  filterFeedback,
  feedbackCsv,
  feedbackState,
  hasAlert,
  type FeedbackRow,
} from "@/lib/feedback";
import { formatParisDate } from "@/lib/parisDateTime";

const number = (v: number | null, digits = 0) =>
  v == null ? "—" : new Intl.NumberFormat("fr-FR", { maximumFractionDigits: digits }).format(v);
const percentage = (v: number | null) => (v == null ? "—" : `${number(v)} %`);
const scoreClass = (value: number | null) =>
  value == null
    ? "bg-gray-100 text-gray-400"
    : value <= 2
      ? "bg-red-50 text-red-700"
      : value >= 4
        ? "bg-emerald-50 text-emerald-800"
        : "bg-amber-50 text-amber-800";
const name = (row: FeedbackRow) =>
  [row.snapshot.client_first_name, row.snapshot.client_last_name].filter(Boolean).join(" ") ||
  "Contact non renseigné";

export function FeedbackDashboard({
  rows,
  initialMonth = "",
  demo = false,
  onFollowup,
  onOpenCase,
  onEmailPreview,
  onSurveyPreview,
}: {
  rows: FeedbackRow[];
  initialMonth?: string;
  demo?: boolean;
  onFollowup: (id: string, status: FeedbackRow["followup_status"], note: string) => Promise<void>;
  onOpenCase?: (caseId: string) => void;
  onEmailPreview?: () => void;
  onSurveyPreview?: () => void;
}) {
  const [month, setMonth] = useState(initialMonth);
  const [center, setCenter] = useState("");
  const [view, setView] = useState<"all" | "answers" | "alerts" | "interests">("answers");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const [limit, setLimit] = useState(20);
  const scoped = useMemo(() => filterFeedback(rows, month, center), [rows, month, center]);
  const metrics = feedbackMetrics(scoped);
  const centers = Array.from(
    new Set(rows.map((r) => r.snapshot.center).filter((c): c is string => !!c)),
  ).sort((a, b) => a.localeCompare(b, "fr"));
  const centerRows = centers
    .map((c) => ({
      name: c,
      metrics: feedbackMetrics(scoped.filter((r) => r.snapshot.center === c)),
    }))
    .filter((c) => c.metrics.answers > 0);
  const visible = scoped
    .filter(
      (r) =>
        (view === "all" ||
          (view === "answers" && r.express_at) ||
          (view === "alerts" && hasAlert(r)) ||
          (view === "interests" && r.express_at && r.cross_selling_optin)) &&
        [name(r), r.snapshot.company_name, r.snapshot.title]
          .join(" ")
          .toLocaleLowerCase("fr")
          .includes(search.toLocaleLowerCase("fr")),
    )
    .sort((a, b) =>
      (b.express_at || b.sent_at || b.snapshot.event_date).localeCompare(
        a.express_at || a.sent_at || a.snapshot.event_date,
      ),
    );
  const current = rows.find((r) => r.id === selected);
  const exportCsv = () => {
    const blob = new Blob([feedbackCsv(visible)], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `feedback-${month || "tous-les-mois"}.csv`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return (
    <div className="mx-auto max-w-[1480px] px-7 py-8 text-gray-900">
      {demo && (
        <div className="mb-5 flex items-center justify-between gap-4 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2.5 text-sm text-indigo-900">
          <span>
            <strong>Preview locale</strong> · Données fictives. Aucun email envoyé.
          </span>
          <div className="flex gap-4">
            {onEmailPreview && (
              <button onClick={onEmailPreview} className="font-medium underline underline-offset-4">
                Voir le bloc email
              </button>
            )}
            {onSurveyPreview && (
              <button
                onClick={onSurveyPreview}
                className="font-medium underline underline-offset-4"
              >
                Tester le formulaire ↗
              </button>
            )}
          </div>
        </div>
      )}
      <div className="mb-7 flex items-start justify-between gap-6">
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-gray-500">
            APRÈS L’ÉVÉNEMENT
          </p>
          <h1 className="text-3xl font-semibold tracking-tight">Satisfaction & retours clients</h1>
          <p className="mt-2 text-sm text-gray-500">
            Comprendre l’expérience, traiter les retours et préparer la prochaine rencontre.
          </p>
        </div>
        <button
          onClick={exportCsv}
          disabled={!visible.length}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium disabled:opacity-40"
        >
          <ArrowDownToLine size={16} />
          Exporter la sélection
        </button>
      </div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-end gap-3">
          <label className="text-xs font-medium text-gray-500">
            Mois de l’événement
            <input
              aria-label="Mois de l’événement"
              type="month"
              value={month}
              onChange={(e) => {
                setMonth(e.target.value);
                setLimit(20);
              }}
              className="mt-1 block rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800"
            />
          </label>
          <label className="text-xs font-medium text-gray-500">
            Centre
            <select
              aria-label="Centre"
              value={center}
              onChange={(e) => {
                setCenter(e.target.value);
                setLimit(20);
              }}
              className="mt-1 block min-w-52 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800"
            >
              <option value="">Tous les centres</option>
              {centers.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </label>
          {(month || center) && (
            <button
              onClick={() => {
                setMonth("");
                setCenter("");
              }}
              className="pb-2 text-xs text-gray-500 underline"
            >
              Réinitialiser
            </button>
          )}
        </div>
        <p className="text-sm text-gray-500">
          <strong className="text-gray-900">{metrics.sent}</strong> invitations ·{" "}
          <strong className="text-gray-900">{metrics.answers}</strong> avis reçus
        </p>
      </div>
      <div className="mb-6 grid grid-cols-3 gap-3 xl:grid-cols-6">
        {[
          {
            title: "Taux de réponse",
            value: percentage(metrics.responseRate),
            detail: `${metrics.answers} avis / ${metrics.sent} invitations`,
          },
          {
            title: "Satisfaction globale",
            value: percentage(metrics.satisfaction),
            detail: "Notes de 4 ou 5 sur 5",
          },
          {
            title: "Accompagnement",
            value: metrics.sales == null ? "—" : `${number(metrics.sales, 1)} /5`,
            detail: "Moyenne commerciale",
          },
          {
            title: "Expérience au centre",
            value: metrics.venue == null ? "—" : `${number(metrics.venue, 1)} /5`,
            detail: "Moyenne terrain",
          },
          {
            title: "Recommandation NPS",
            value:
              metrics.nps == null ? "—" : `${metrics.nps > 0 ? "+" : ""}${number(metrics.nps)}`,
            detail: `${metrics.npsCount} réponses · de −100 à +100`,
          },
          {
            title: "Intérêt autres centres",
            value: number(metrics.interests),
            detail: "Demandes explicites uniquement",
          },
        ].map((k) => (
          <div key={k.title} className="rounded-xl border border-gray-200 bg-white p-4">
            <p className="text-xs font-medium text-gray-500">{k.title}</p>
            <p className="mt-3 text-3xl font-semibold tabular-nums tracking-tight">{k.value}</p>
            <p className="mt-2 text-[11px] text-gray-500">{k.detail}</p>
          </div>
        ))}
      </div>
      {metrics.alerts > 0 && (
        <button
          onClick={() => setView("alerts")}
          className="mb-6 flex w-full items-center justify-between rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-left text-red-900"
        >
          <span className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-lg font-semibold">
              {metrics.alerts}
            </span>
            <span className="block">
              <strong className="block text-sm">
                {metrics.alerts === 1
                  ? "Un retour demande votre attention"
                  : "Des retours demandent votre attention"}
              </strong>
              <span className="text-xs text-red-700">
                Note globale de 1 ou 2 sur 5 · suivi encore ouvert
              </span>
            </span>
          </span>
          <span className="inline-flex items-center gap-2 text-sm font-medium">
            Voir les avis
            <ChevronRight size={16} />
          </span>
        </button>
      )}
      <div className="mb-7 grid grid-cols-2 gap-5">
        <section className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-sm font-semibold">Les notes de vos clients</h2>
            <span className="text-xs text-gray-400">{metrics.answers} avis</span>
          </div>
          <div className="space-y-2.5">
            {metrics.distribution.map((d) => (
              <div key={d.score} className="flex items-center gap-3 text-xs">
                <span className="inline-flex w-8 shrink-0 items-center gap-1 text-gray-600">
                  {d.score}
                  <Star size={11} fill="currentColor" />
                </span>
                <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-gray-100">
                  <div
                    style={{ width: `${metrics.answers ? (d.count / metrics.answers) * 100 : 0}%` }}
                    className={`h-full rounded-full ${d.score >= 4 ? "bg-indigo-400" : d.score === 3 ? "bg-amber-400" : "bg-red-400"}`}
                  />
                </div>
                <span className="w-5 text-right tabular-nums text-gray-600">{d.count}</span>
              </div>
            ))}
          </div>
        </section>
        <section className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-sm font-semibold">Satisfaction par centre</h2>
            <span className="text-xs text-gray-400">Part des notes ≥ 4/5</span>
          </div>
          {centerRows.length ? (
            <div className="space-y-3">
              {centerRows.map((c) => (
                <button
                  onClick={() => setCenter(c.name)}
                  key={c.name}
                  className="flex w-full items-center gap-3 text-left text-xs"
                >
                  <span className="w-36 truncate text-gray-700">{c.name}</span>
                  <div className="h-2 flex-1 rounded-full bg-gray-100">
                    <div
                      className="h-full rounded-full bg-indigo-300"
                      style={{ width: `${c.metrics.satisfaction || 0}%` }}
                    />
                  </div>
                  <strong className="w-10 text-right font-medium tabular-nums">
                    {percentage(c.metrics.satisfaction)}
                  </strong>
                  <span className="w-12 text-right text-gray-400">{c.metrics.answers} avis</span>
                </button>
              ))}
            </div>
          ) : (
            <p className="py-9 text-center text-sm text-gray-400">Aucun avis sur cette période.</p>
          )}
        </section>
      </div>
      <section className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-5 py-4">
          <div className="flex gap-1">
            {(
              [
                { id: "answers", label: "Avis reçus", count: metrics.answers },
                { id: "alerts", label: "À traiter", count: metrics.alerts },
                { id: "interests", label: "Autres centres", count: metrics.interests },
                { id: "all", label: "Toutes les invitations", count: scoped.length },
              ] as const
            ).map((tab) => (
              <button
                key={tab.id}
                aria-pressed={view === tab.id}
                onClick={() => {
                  setView(tab.id);
                  setLimit(20);
                }}
                className={`rounded-lg px-3 py-2 text-xs font-medium ${view === tab.id ? "bg-indigo-50 text-indigo-800" : "text-gray-500 hover:bg-gray-50"}`}
              >
                {tab.label} <span className="ml-1 opacity-60">{tab.count}</span>
              </button>
            ))}
          </div>
          <input
            type="search"
            aria-label="Rechercher un retour"
            placeholder="Rechercher un client, une entreprise…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-72 rounded-lg border border-gray-200 px-3 py-2 text-xs"
          />
        </div>
        {!visible.length ? (
          <div className="py-14 text-center">
            <MessageSquare className="mx-auto mb-3 text-gray-300" />
            <h3 className="text-sm font-medium">Aucun retour dans cette sélection</h3>
            <p className="mt-1 text-xs text-gray-500">
              Changez la période, le centre ou le filtre pour retrouver les autres invitations.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500">
                <tr>
                  {[
                    "Client & événement",
                    "Centre",
                    "Globale",
                    "Commercial / terrain",
                    "NPS",
                    "Suivi",
                    "",
                  ].map((h, i) => (
                    <th key={i} className="px-5 py-3 font-medium">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.slice(0, limit).map((r) => (
                  <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50/60">
                    <td className="px-5 py-4">
                      <button
                        onClick={() => setSelected(r.id)}
                        className="text-left font-medium hover:text-indigo-700"
                      >
                        {name(r)}
                      </button>
                      <p className="mt-0.5 text-xs text-gray-500">
                        {r.snapshot.company_name || r.snapshot.title} ·{" "}
                        {formatParisDate(r.snapshot.event_date)}
                      </p>
                    </td>
                    <td className="px-5 py-4 text-xs text-gray-600">
                      {r.snapshot.center || "Non renseigné"}
                    </td>
                    <td className="px-5 py-4">
                      <span
                        className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold ${scoreClass(r.overall)}`}
                      >
                        {r.overall ?? "—"}
                        {r.overall != null && <Star size={11} fill="currentColor" />}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-xs tabular-nums text-gray-600">
                      {r.sales ?? "—"} /5 <span className="mx-2 text-gray-300">·</span>{" "}
                      {r.venue ?? "—"} /5
                    </td>
                    <td className="px-5 py-4 text-xs font-medium tabular-nums">
                      {r.nps != null ? `${r.nps}/10` : "—"}
                    </td>
                    <td className="px-5 py-4 text-xs">
                      {hasAlert(r) ? (
                        <span className="rounded-full bg-red-50 px-2 py-1 text-red-700">
                          {r.followup_status === "in_progress" ? "En cours" : "À contacter"}
                        </span>
                      ) : r.followup_status === "resolved" ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700">
                          <Check size={12} />
                          Traité
                        </span>
                      ) : (
                        <span className="text-gray-500">{feedbackState(r)}</span>
                      )}
                      {r.cross_selling_optin && (
                        <p className="mt-1 text-[11px] text-indigo-600">Intérêt autres centres</p>
                      )}
                    </td>
                    <td className="px-3 py-4">
                      <button
                        onClick={() => setSelected(r.id)}
                        aria-label={`Voir le retour de ${name(r)}`}
                        className="rounded-md p-2 text-gray-400 hover:bg-indigo-50 hover:text-indigo-700"
                      >
                        <ChevronRight size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex items-center justify-between border-t border-gray-100 px-5 py-3 text-xs text-gray-400">
          <span>
            {Math.min(limit, visible.length)} sur {visible.length} · Cohorte du mois de l’événement
          </span>
          {visible.length > limit && (
            <button onClick={() => setLimit((n) => n + 20)} className="text-indigo-700">
              Afficher davantage
            </button>
          )}
        </div>
      </section>
      <p className="mt-3 text-[11px] leading-relaxed text-gray-400">
        NPS = % de promoteurs (9–10) − % de détracteurs (0–6). Les brouillons et les questionnaires
        non validés ne comptent pas comme des réponses.
      </p>
      {current && (
        <FeedbackDetail
          key={current.id}
          row={current}
          onClose={() => setSelected(null)}
          onSave={onFollowup}
          onOpenCase={onOpenCase}
        />
      )}
    </div>
  );
}

function FeedbackDetail({
  row,
  onClose,
  onSave,
  onOpenCase,
}: {
  row: FeedbackRow;
  onClose: () => void;
  onSave: (id: string, status: FeedbackRow["followup_status"], note: string) => Promise<void>;
  onOpenCase?: ((id: string) => void) | undefined;
}) {
  const [status, setStatus] = useState(row.followup_status);
  const [note, setNote] = useState(row.staff_note || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const dialog = useRef<HTMLElement>(null);
  const dirty = !saved && (status !== row.followup_status || note !== (row.staff_note || ""));
  const close = () => {
    if (!busy && (!dirty || window.confirm("Fermer sans enregistrer les modifications du suivi ?")))
      onClose();
  };
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = overflow;
      previous?.focus();
    };
  }, []);
  const save = async () => {
    setBusy(true);
    setError("");
    try {
      await onSave(row.id, status, note);
      setSaved(true);
    } catch {
      setError("Le suivi n’a pas été enregistré. Vos notes sont conservées ici.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-gray-950/35" onClick={close}>
      <section
        ref={dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-detail-title"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === "Escape") close();
          if (e.key === "Tab") {
            const elements = Array.from(
              dialog.current?.querySelectorAll<HTMLElement>(
                "button:not(:disabled),select,textarea,input,a[href]",
              ) || [],
            );
            const first = elements[0],
              last = elements[elements.length - 1];
            if (e.shiftKey && document.activeElement === first) {
              e.preventDefault();
              last?.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
              e.preventDefault();
              first?.focus();
            }
          }
        }}
        className="flex h-full w-[560px] flex-col bg-white shadow-2xl"
      >
        <header className="flex items-center justify-between border-b px-6 py-5">
          <div>
            <p className="text-xs text-gray-500">RETOUR CLIENT</p>
            <h2 id="feedback-detail-title" className="mt-1 text-xl font-semibold">
              {name(row)}
            </h2>
          </div>
          <button autoFocus aria-label="Fermer le retour" onClick={close} className="p-2">
            <X size={20} />
          </button>
        </header>
        <div className="flex-1 space-y-6 overflow-y-auto p-6">
          <div>
            <p className="font-medium">{row.snapshot.company_name || row.snapshot.title}</p>
            <p className="mt-1 text-sm text-gray-500">
              {row.snapshot.center} · {formatParisDate(row.snapshot.event_date)}
            </p>
            {onOpenCase && (
              <button
                onClick={() => onOpenCase(row.case_id)}
                className="mt-2 inline-flex items-center gap-1 text-xs text-indigo-700"
              >
                Ouvrir le dossier
                <ArrowUpRight size={13} />
              </button>
            )}
          </div>
          <div className="grid grid-cols-4 gap-2">
            {[
              ["Globale", row.overall],
              ["Commercial", row.sales],
              ["Terrain", row.venue],
              ["NPS", row.nps],
            ].map(([label, value]) => (
              <div key={label} className="rounded-lg bg-gray-50 p-3">
                <p className="text-[11px] text-gray-500">{label}</p>
                <p className="mt-1 text-xl font-semibold">
                  {value ?? "—"}
                  <span className="text-xs font-normal text-gray-400">
                    /{label === "NPS" ? 10 : 5}
                  </span>
                </p>
              </div>
            ))}
          </div>
          <div>
            <h3 className="mb-2 text-sm font-semibold">Ce que le client nous dit</h3>
            <p className="whitespace-pre-wrap rounded-lg border border-gray-200 p-4 text-sm leading-relaxed">
              {row.improvement || "Aucun commentaire complémentaire."}
            </p>
            <p className="mt-3 text-xs text-gray-500">
              Activités appréciées : {row.activities.join(", ") || "Non renseignées"}
            </p>
            <p className="mt-2 text-xs text-gray-500">
              Restauration :{" "}
              {row.catering_na
                ? "Non concerné"
                : row.catering != null
                  ? `${row.catering}/5`
                  : "Non renseignée"}
            </p>
          </div>
          <div className="rounded-lg bg-indigo-50 p-4 text-sm text-indigo-900">
            <p>
              {row.cross_selling_optin
                ? "✓ Souhaite recevoir des informations sur les autres centres"
                : "Aucune demande d’informations sur les autres centres"}
            </p>
            <p className="mt-2 text-xs">
              Témoignage :{" "}
              {row.testimonial_consent ? "utilisation autorisée" : "utilisation non autorisée"}
            </p>
          </div>
          <div className="border-t pt-5">
            <h3 className="mb-3 text-sm font-semibold">Suivi par l’équipe</h3>
            <label className="block text-xs text-gray-500">
              Statut du suivi
              <select
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value as typeof status);
                  setSaved(false);
                }}
                className="mt-1 block w-full rounded-lg border p-2.5 text-sm text-gray-800"
              >
                <option value="open">À contacter</option>
                <option value="in_progress">En cours</option>
                <option value="resolved">Traité</option>
              </select>
            </label>
            <label className="mt-4 block text-xs text-gray-500">
              Notes de suivi internes
              <textarea
                rows={4}
                maxLength={5000}
                value={note}
                onChange={(e) => {
                  setNote(e.target.value);
                  setSaved(false);
                }}
                className="mt-1 block w-full rounded-lg border p-3 text-sm text-gray-800"
                placeholder="Appel effectué, réponse apportée, prochaine action…"
              />
            </label>
          </div>
          {error && (
            <p role="alert" className="text-sm text-red-700">
              {error}
            </p>
          )}
          {saved && (
            <p role="status" className="text-sm text-emerald-700">
              Suivi enregistré.
            </p>
          )}
        </div>
        <footer className="flex justify-end border-t p-4">
          <button
            onClick={save}
            disabled={busy}
            className="rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white disabled:opacity-50"
          >
            {busy ? "Enregistrement…" : "Enregistrer le suivi"}
          </button>
        </footer>
      </section>
    </div>
  );
}
